import React, { useContext, useState } from "react";
import { toast } from "react-toastify";
import { Tooltip, Drawer, Modal } from "antd";
import {
    EyeFilled,
    EyeInvisibleFilled,

    SaveOutlined,
    EyeOutlined,
    UserOutlined,
    PhoneOutlined,
    MailOutlined,
    IdcardOutlined,
    BankOutlined,
    QrcodeOutlined,

    BgColorsOutlined,
    UpOutlined,
    DownOutlined,
    SwapOutlined,
    InfoCircleOutlined,
    GlobalOutlined,
} from "@ant-design/icons";
import BusinessCardScanner from "./BusinessCardScanner";
import HomeCardFace from "./HomeCardFace";
import { getArabicTexts, applyArabicEdit } from "./arabicCard";
import TextStyleControl from "../settings/TextStyleControl";
import ThemeCustomizer from "../shared/ThemeCustomizer";
import { salutations } from "../../Constants";
import SocialProfilesEditor from "./SocialProfilesEditor";
import { getSocialLinks, setSocialLink } from "./socialPlatforms";
import { BriefcaseIcon } from "@heroicons/react/24/outline";
import AppContext from "../../AppContext";
import { EditFormContext } from "./PublicEmployeeEditForm";
import CardPreview from "../shared/CardPreview";
import ImageCustomizationDialog from "../employee/ImageCustomizationDialog";
import { useTranslation } from "react-i18next";


// Visibility Toggle Button - Compact on mobile, expanded on desktop
const VisibilityToggle = ({ isVisible, onClick, icon: Icon, label }) => (
    <button
        type="button"
        onClick={onClick}
        className={`flex flex-col items-center justify-center p-1 sm:p-2 min-w-[40px] sm:min-w-[44px] min-h-[40px] sm:min-h-[44px] rounded-md transition-all flex-shrink-0 ${isVisible
            ? "bg-indigo-600 text-white shadow-sm"
            : "bg-gray-200 text-gray-500"
            } active:scale-95`}
        title={label}
        aria-label={label}
    >
        {Icon && <Icon className="text-sm sm:text-base" />}
        {isVisible ? (
            <EyeFilled className="text-[8px] sm:text-[10px] mt-0.5" />
        ) : (
            <EyeInvisibleFilled className="text-[8px] sm:text-[10px] mt-0.5" />
        )}
        {/* Label always visible but smaller on mobile */}
        <span className="text-[8px] sm:text-[9px] mt-0.5 leading-none whitespace-nowrap">
            {label}
        </span>
    </button>
);

const HomeCard = () => {
    const { t } = useTranslation();
    const authCtx = useContext(AppContext);
    const editFormContext = useContext(EditFormContext);

    const cardData = editFormContext?.cardData;
    const setCardData = editFormContext?.setCardData;
    const handleCardSubmit = editFormContext?.handleCardSubmit;
    const formData = editFormContext?.formData;
    const setFormData = editFormContext?.setFormData;

    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [imageDialogue, setImageDialogue] = useState(false);
    const [cropFile, setCropFile] = useState(null);
    const [imageType, setImageType] = useState(null); // 'profile' or 'logo'
    const [isImageLoading, setIsImageLoading] = useState(false);
    const [showEditHint, setShowEditHint] = useState(true);
    const [showStyleSettings, setShowStyleSettings] = useState(false);
    const [showFineTune, setShowFineTune] = useState(false);
    const [socialModalOpen, setSocialModalOpen] = useState(false);
    const [showDashboardColors, setShowDashboardColors] = useState(false);
    const [activeTab, setActiveTab] = useState('myCard');
    const [side, setSide] = useState('en'); // 'en' | 'ar'


    // Handle field changes
    const handleChange = (name, value) => {
        if (setCardData) {
            setCardData((prev) => ({ ...prev, [name]: value }));
            setHasChanges(true);
        }
    };

    // Arabic side: name, designation and company live in formData.arabicContent
    const handleArabicTextChange = (field, value) => {
        if (setFormData) {
            setFormData((prev) => applyArabicEdit(prev, field, value));
            setHasChanges(true);
        }
    };

    // TextStyleControl calls setCardData directly; this keeps the Save button in step
    const setCardDataTracked = (updater) => {
        setCardData(updater);
        setHasChanges(true);
    };

    const handleSocialChange = (platform, url) => {
        if (setFormData) {
            setFormData((prev) => setSocialLink(prev, platform, url));
            setHasChanges(true);
        }
    };

    const handleSalutationChange = (value) => {
        if (setFormData) {
            setFormData((prev) => ({ ...prev, salutation: value }));
            setHasChanges(true);
        }
    };

    // Handle visibility toggle
    const handleVisibilityToggle = (field) => {
        if (setCardData) {
            const isCurrentlyVisible = cardData?.[`${field}Visible`] ?? true;
            setCardData((prev) => ({
                ...prev,
                [`${field}Visible`]: !isCurrentlyVisible,
            }));
            setHasChanges(true);
            // Visual feedback
            const fieldName = field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1');
            toast.info(
                `${fieldName} ${!isCurrentlyVisible ? 'shown' : 'hidden'}`,
                { autoClose: 1500, hideProgressBar: true }
            );
        }
    };

    // Handle profile image change
    const handleProfileImageChange = (e) => {
        if (e.target.files.length > 0) {
            setIsImageLoading(true);
            const selectedFile = e.target.files[0];
            const fileSizeInKB = selectedFile.size / 1024;

            if (fileSizeInKB > 1024) {
                toast.error("Image size must be less than 1MB");
                e.target.value = "";
                setIsImageLoading(false);
                return;
            }

            const reader = new FileReader();
            reader.onload = () => {
                setCropFile(reader.result);
                setImageType('profile');
                setIsImageLoading(false);
            };
            reader.readAsDataURL(selectedFile);
            setImageDialogue(true);
            e.target.value = null;
        }
    };

    // Handle company logo change
    const handleLogoImageChange = (e) => {
        if (e.target.files.length > 0) {
            setIsImageLoading(true);
            const selectedFile = e.target.files[0];
            const fileSizeInKB = selectedFile.size / 1024;

            if (fileSizeInKB > 1024) {
                toast.error("Image size must be less than 1MB");
                e.target.value = "";
                setIsImageLoading(false);
                return;
            }

            const reader = new FileReader();
            reader.onload = () => {
                setCropFile(reader.result);
                setImageType('logo');
                setIsImageLoading(false);
            };
            reader.readAsDataURL(selectedFile);
            setImageDialogue(true);
            e.target.value = null;
        }
    };

    // Handle cropped image save
    const handleFileCropSave = (file) => {
        if (imageType === 'profile') {
            handleChange("profileImage", file);
            handleChange("profileImagePreview", URL.createObjectURL(file));
            toast.success(t("common.imageUpdated") || "Profile image updated!");
        } else if (imageType === 'logo') {
            handleChange("companyLogo", file);
            handleChange("companyLogoUrl", URL.createObjectURL(file));
            toast.success(t("common.imageUpdated") || "Logo updated!");
        }
        setCropFile(null);
        setImageDialogue(false);
        setImageType(null);
        setHasChanges(true);
    };

    // Handle save
    const handleSubmit = async () => {
        if (!handleCardSubmit) return;

        setIsSubmitting(true);
        try {
            await handleCardSubmit(cardData);
            setHasChanges(false);
        } catch (error) {
            toast.error(t("common.saveFailed") || "Save failed");
        }
        setIsSubmitting(false);
    };


    const isArabicSide = side === "ar";
    const arabicTexts = getArabicTexts(formData);
    const socialLinks = getSocialLinks(formData);
    const reducedMotion =
        typeof window !== "undefined" &&
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const placeholders = {
        name: t("form.name") || "Enter name",
        designation: t("form.role") || "Enter designation",
        phone: t("form.phone") || "Enter phone",
        email: t("form.email") || "Enter email",
        employeeId: t("form.employeeID") || "Enter employee ID",
        companyName: t("form.company") || "Enter company",
    };

    // The turned-away face stays laid out (so the card keeps its height) but is hidden after the
    // turn finishes, so it can't be tabbed into or read out.
    const faceStyle = (active, flipped) => ({
        gridArea: "1 / 1",
        backfaceVisibility: "hidden",
        WebkitBackfaceVisibility: "hidden",
        transform: flipped && !reducedMotion ? "rotateY(180deg)" : undefined,
        visibility: active ? "visible" : "hidden",
        opacity: reducedMotion ? (active ? 1 : 0) : undefined,
        pointerEvents: active ? undefined : "none",
        transition: reducedMotion
            ? `opacity 0.2s, visibility 0s linear ${active ? "0s" : "0.2s"}`
            : `visibility 0s linear ${active ? "0s" : "0.6s"}`,
    });

    // Preview shows whichever side is open; blank Arabic fields fall back to English,
    // the same way the public card does.
    const previewCardData = isArabicSide
        ? {
            ...cardData,
            name: arabicTexts.name || cardData?.name,
            designation: arabicTexts.designation || cardData?.designation,
            companyName: arabicTexts.companyName || cardData?.companyName,
        }
        : cardData;

    if (!cardData) {
        return (
            <div className="flex justify-center items-center min-h-[400px]">
                <div className="text-lg text-gray-600">{t("common.loading") || "Loading..."}</div>
            </div>
        );
    }

    return (
        <div className="flex flex-col items-center px-0 pt-6 pb-4 sm:p-4 md:p-6 lg:p-10 xl:p-12 min-h-screen bg-gray-50">
            {/* Image Cropping Dialog */}
            <ImageCustomizationDialog
                showDialog={imageDialogue}
                image={cropFile}
                onClose={() => setImageDialogue(false)}
                handleFileSave={handleFileCropSave}
            />

            {/* Main Content Container - keeps everything aligned */}
            <div className="flex flex-col items-center w-full">
                <div className="flex space-x-4 mb-6">
                    <button
                        type="button"
                        onClick={() => setActiveTab('myCard')}
                        className={`px-4 py-2 font-medium text-sm rounded-lg transition-colors ${activeTab === 'myCard'
                            ? 'bg-indigo-600 text-white shadow-md'
                            : 'bg-white text-gray-600 hover:bg-gray-100'
                            }`}
                    >
                        My Card
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveTab('businessCard')}
                        className={`px-4 py-2 font-medium text-sm rounded-lg transition-colors ${activeTab === 'businessCard'
                            ? 'bg-indigo-600 text-white shadow-md'
                            : 'bg-white text-gray-600 hover:bg-gray-100'
                            }`}
                    >
                        {t("tabs.businessCards") || "Business Card"}
                    </button>
                </div>

                {/* --- Condition 1: Show My Card (Existing Content) --- */}
                {activeTab === 'myCard' && (
                    <>

                        {/* Greeting Section */}
                        <div className="w-full mx-2 sm:mx-0 sm:max-w-[500px] lg:max-w-[600px] mb-4 lg:mb-6 text-center">
                            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800">
                                {t("homeCard.greeting.hello")}{cardData?.name ? `, ${cardData.name.split(' ')[0]}` : ''} 👋
                            </h1>
                            <p className="text-sm text-gray-500 mt-1">
                                {t("homeCard.greeting.subtitle")}
                            </p>
                        </div>

                        {/* Helpful Hint Banner */}
                        {showEditHint && (
                            <div className="w-full mx-2 sm:mx-0 sm:max-w-[500px] lg:max-w-[700px] mb-4 lg:mb-6 bg-indigo-50 border border-indigo-200 rounded-lg p-3 lg:p-4 flex items-start gap-2 lg:gap-3 shadow-sm">
                                <div className="flex-1 text-sm text-indigo-900">
                                    <strong className="block mb-1">💡 {t("homeCard.tips.title")}</strong>
                                    <ul className="space-y-0.5 text-xs">
                                        <li>• {t("homeCard.tips.tapToEdit")}</li>
                                        <li>• {t("homeCard.tips.tapImages")}</li>
                                        <li>• {t("homeCard.tips.useToggles")}</li>
                                        <li>• {t("homeCard.tips.useStyle")}</li>
                                    </ul>
                                    <p className="mt-2 text-[10px] text-indigo-600 border-t border-indigo-200 pt-2">
                                        💼 {t("homeCard.tips.advancedNote")}
                                    </p>
                                </div>
                                <button
                                    onClick={() => setShowEditHint(false)}
                                    className="text-indigo-400 hover:text-indigo-600 text-lg leading-none p-1"
                                    aria-label={t("common.close")}
                                >
                                    ×
                                </button>
                            </div>
                        )}

                        {/* Card Editor Section */}
                        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 lg:gap-8 xl:gap-10 items-center sm:items-start justify-center w-full sm:max-w-none sm:w-auto lg:bg-white lg:rounded-2xl lg:shadow-lg lg:p-6 xl:p-8">
                            {/* Visibility Settings Bar + Style Button Container */}
                            <div className="flex flex-col items-center sm:items-start w-full sm:w-auto mx-2 sm:mx-0">
                                {/* Visibility Toggles */}
                                <div className="flex sm:flex-col gap-1.5 sm:gap-1 lg:gap-1.5 bg-white lg:bg-gray-50 rounded-lg shadow-md lg:shadow-none lg:border lg:border-gray-200 p-1.5 sm:p-2 lg:p-3 w-full sm:w-auto overflow-x-auto sm:overflow-x-visible flex-shrink-0">
                                    <VisibilityToggle
                                        isVisible={cardData?.companyLogoVisible ?? true}
                                        onClick={() => handleVisibilityToggle("companyLogo")}
                                        icon={BankOutlined}
                                        label={t("form.logo") || "Logo"}
                                    />
                                    <VisibilityToggle
                                        isVisible={cardData?.profileImageVisible ?? true}
                                        onClick={() => handleVisibilityToggle("profileImage")}
                                        icon={UserOutlined}
                                        label={t("form.photo") || "Photo"}
                                    />
                                    <VisibilityToggle
                                        isVisible={cardData?.nameVisible ?? true}
                                        onClick={() => handleVisibilityToggle("name")}
                                        icon={UserOutlined}
                                        label={t("form.name") || "Name"}
                                    />
                                    <VisibilityToggle
                                        isVisible={cardData?.designationVisible ?? true}
                                        onClick={() => handleVisibilityToggle("designation")}
                                        icon={() => <BriefcaseIcon className="h-4 w-4" />}
                                        label={t("form.role") || "Role"}
                                    />
                                    <VisibilityToggle
                                        isVisible={cardData?.phoneVisible ?? true}
                                        onClick={() => handleVisibilityToggle("phone")}
                                        icon={PhoneOutlined}
                                        label={t("form.phone") || "Phone"}
                                    />
                                    <VisibilityToggle
                                        isVisible={cardData?.emailVisible ?? true}
                                        onClick={() => handleVisibilityToggle("email")}
                                        icon={MailOutlined}
                                        label={t("form.email") || "Email"}
                                    />
                                    <VisibilityToggle
                                        isVisible={cardData?.employeeIdVisible ?? true}
                                        onClick={() => handleVisibilityToggle("employeeId")}
                                        icon={IdcardOutlined}
                                        label={t("form.id") || "ID"}
                                    />
                                    <VisibilityToggle
                                        isVisible={cardData?.companyNameVisible ?? true}
                                        onClick={() => handleVisibilityToggle("companyName")}
                                        icon={BankOutlined}
                                        label={t("form.company") || "Company"}
                                    />
                                    <VisibilityToggle
                                        isVisible={cardData?.qrCodeVisible ?? true}
                                        onClick={() => handleVisibilityToggle("qrCode")}
                                        icon={QrcodeOutlined}
                                        label={t("form.qr") || "QR"}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setSocialModalOpen(true)}
                                        aria-label={t("homeCard.social.title") || "Social profiles"}
                                        className={`flex flex-col items-center justify-center p-1 sm:p-2 min-w-[40px] sm:min-w-[44px] min-h-[40px] sm:min-h-[44px] rounded-md transition-all flex-shrink-0 active:scale-95 ${socialLinks.length > 0
                                            ? "bg-indigo-600 text-white shadow-sm"
                                            : "bg-gray-200 text-gray-500"
                                            }`}
                                        title={t("homeCard.social.title") || "Social profiles"}
                                    >
                                        <GlobalOutlined className="text-sm sm:text-base" />
                                        <span className="text-[8px] sm:text-[9px] mt-0.5 leading-none whitespace-nowrap">
                                            {t("homeCard.social.title") || "Social"}
                                        </span>
                                    </button>
                                </div>

                                {/* Style Settings Button - Below visibility toggles */}
                                <button
                                    type="button"
                                    onClick={() => setShowStyleSettings(!showStyleSettings)}
                                    className={`mt-2 lg:mt-3 flex items-center justify-center gap-2 px-4 py-2 lg:py-2.5 w-full sm:w-auto rounded-lg transition-all ${showStyleSettings
                                        ? "bg-purple-600 text-white shadow-md"
                                        : "bg-white text-gray-600 shadow-md hover:bg-gray-50"
                                        } active:scale-95`}
                                    title={t("settings.theme.title") || "Style"}
                                    aria-label={t("settings.theme.title") || "Style"}
                                >
                                    <BgColorsOutlined className="text-base" />
                                    <span className="text-sm font-medium">
                                        {t("digitalCard.tabs.style") || "Style"}
                                    </span>
                                    {showStyleSettings ? (
                                        <UpOutlined className="text-xs" />
                                    ) : (
                                        <DownOutlined className="text-xs" />
                                    )}
                                </button>

                                {/* Style Settings Panel - Shows below the Style button */}
                                {showStyleSettings && (
                                    <div className="w-full sm:w-auto lg:w-72 xl:w-80 mt-2 lg:mt-3 bg-white lg:bg-gray-50 rounded-lg shadow-md lg:shadow-none lg:border lg:border-gray-200 p-4 lg:p-5 animate-fadeIn">
                                        {/* Quick Presets */}
                                        <div className="mb-4">
                                            <label className="block text-xs font-medium text-gray-600 mb-2">
                                                {t("digitalCard.style.quickPresets") || "Quick Presets"}
                                            </label>
                                            <div className="flex flex-wrap gap-2">
                                                {[
                                                    { bg: "#000147", text: "#ffffff", divider: "#e5e7eb", name: "Navy" },
                                                    { bg: "#1a1a2e", text: "#ffffff", divider: "#4a4a6a", name: "Dark" },
                                                    { bg: "#0f4c75", text: "#ffffff", divider: "#3282b8", name: "Ocean" },
                                                    { bg: "#2d3436", text: "#dfe6e9", divider: "#636e72", name: "Slate" },
                                                    { bg: "#6c5ce7", text: "#ffffff", divider: "#a29bfe", name: "Purple" },
                                                    { bg: "#00695c", text: "#ffffff", divider: "#4db6ac", name: "Teal" },
                                                ].map((preset) => (
                                                    <button
                                                        key={preset.name}
                                                        type="button"
                                                        onClick={() => {
                                                            handleChange("backgroundColor", preset.bg);
                                                            handleChange("dividerColor", preset.divider);
                                                            setCardData((prev) => ({
                                                                ...prev,
                                                                textStyles: {
                                                                    ...prev?.textStyles,
                                                                    name: { ...prev?.textStyles?.name, color: preset.text },
                                                                    designation: { ...prev?.textStyles?.designation, color: preset.text },
                                                                    phone: { ...prev?.textStyles?.phone, color: preset.text },
                                                                    email: { ...prev?.textStyles?.email, color: preset.text },
                                                                    employeeId: { ...prev?.textStyles?.employeeId, color: preset.text },
                                                                    companyName: { ...prev?.textStyles?.companyName, color: preset.text },
                                                                },
                                                            }));
                                                            setHasChanges(true);
                                                        }}
                                                        className="flex items-center gap-1.5 px-2 py-1 rounded border border-gray-200 hover:border-gray-400 transition-colors"
                                                        title={preset.name}
                                                    >
                                                        <span
                                                            className="w-4 h-4 rounded-full border border-gray-300"
                                                            style={{ backgroundColor: preset.bg }}
                                                        />
                                                        <span className="text-[10px] text-gray-600">{preset.name}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="border-t pt-3 space-y-3">
                                            {/* Background */}
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1.5">
                                                    {t("digitalCard.style.background") || "Background"}
                                                </label>
                                                <div className="flex items-center gap-2 mb-2">
                                                    <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                                                        <input
                                                            type="radio"
                                                            checked={!cardData?.useBackgroundImage}
                                                            onChange={() => handleChange("useBackgroundImage", false)}
                                                            className="w-3 h-3"
                                                        />
                                                        {t("digitalCard.style.color") || "Color"}
                                                    </label>
                                                    <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                                                        <input
                                                            type="radio"
                                                            checked={cardData?.useBackgroundImage || false}
                                                            onChange={() => handleChange("useBackgroundImage", true)}
                                                            className="w-3 h-3"
                                                        />
                                                        {t("digitalCard.style.image") || "Image"}
                                                    </label>
                                                </div>
                                                {!cardData?.useBackgroundImage ? (
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            type="color"
                                                            value={cardData?.backgroundColor || "#000147"}
                                                            onChange={(e) => handleChange("backgroundColor", e.target.value)}
                                                            className="h-8 w-10 rounded cursor-pointer border border-gray-300"
                                                        />
                                                        <input
                                                            type="text"
                                                            value={cardData?.backgroundColor || "#000147"}
                                                            onChange={(e) => handleChange("backgroundColor", e.target.value)}
                                                            className="flex-1 px-2 py-1.5 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                            placeholder="#000147"
                                                        />
                                                    </div>
                                                ) : (
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={(e) => {
                                                            const file = e.target.files?.[0];
                                                            if (file) {
                                                                const reader = new FileReader();
                                                                reader.onloadend = () => {
                                                                    handleChange("backgroundImage", file);
                                                                    handleChange("backgroundImageUrl", reader.result);
                                                                };
                                                                reader.readAsDataURL(file);
                                                            }
                                                        }}
                                                        className="w-full text-xs file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
                                                    />
                                                )}
                                            </div>

                                            {/* Text Color */}
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1.5">
                                                    {t("settings.theme.primaryTextColor") || "Text Color"}
                                                </label>
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="color"
                                                        value={cardData?.textStyles?.name?.color || "#ffffff"}
                                                        onChange={(e) => {
                                                            const color = e.target.value;
                                                            setCardData((prev) => ({
                                                                ...prev,
                                                                textStyles: {
                                                                    ...prev?.textStyles,
                                                                    name: { ...prev?.textStyles?.name, color },
                                                                    designation: { ...prev?.textStyles?.designation, color },
                                                                    phone: { ...prev?.textStyles?.phone, color },
                                                                    email: { ...prev?.textStyles?.email, color },
                                                                    employeeId: { ...prev?.textStyles?.employeeId, color },
                                                                    companyName: { ...prev?.textStyles?.companyName, color },
                                                                },
                                                            }));
                                                            setHasChanges(true);
                                                        }}
                                                        className="h-8 w-10 rounded cursor-pointer border border-gray-300"
                                                    />
                                                    <input
                                                        type="text"
                                                        value={cardData?.textStyles?.name?.color || "#ffffff"}
                                                        onChange={(e) => {
                                                            const color = e.target.value;
                                                            setCardData((prev) => ({
                                                                ...prev,
                                                                textStyles: {
                                                                    ...prev?.textStyles,
                                                                    name: { ...prev?.textStyles?.name, color },
                                                                    designation: { ...prev?.textStyles?.designation, color },
                                                                    phone: { ...prev?.textStyles?.phone, color },
                                                                    email: { ...prev?.textStyles?.email, color },
                                                                    employeeId: { ...prev?.textStyles?.employeeId, color },
                                                                    companyName: { ...prev?.textStyles?.companyName, color },
                                                                },
                                                            }));
                                                            setHasChanges(true);
                                                        }}
                                                        className="flex-1 px-2 py-1.5 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                        placeholder="#ffffff"
                                                    />
                                                </div>
                                            </div>

                                            {/* Divider Color */}
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1.5">
                                                    {t("digitalCard.style.dividerLineStyle") || "Divider Color"}
                                                </label>
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="color"
                                                        value={cardData?.dividerColor || "#e5e7eb"}
                                                        onChange={(e) => handleChange("dividerColor", e.target.value)}
                                                        className="h-8 w-10 rounded cursor-pointer border border-gray-300"
                                                    />
                                                    <input
                                                        type="text"
                                                        value={cardData?.dividerColor || "#e5e7eb"}
                                                        onChange={(e) => handleChange("dividerColor", e.target.value)}
                                                        className="flex-1 px-2 py-1.5 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                        placeholder="#e5e7eb"
                                                    />
                                                </div>
                                            </div>

                                            {/* Dashboard colors: header and accent colors of the app, not of the card */}
                                            <button
                                                type="button"
                                                onClick={() => setShowDashboardColors(true)}
                                                className="w-full text-left text-xs font-medium text-gray-600 hover:text-gray-900"
                                            >
                                                {t("homeCard.dashboardColors")}
                                            </button>

                                            {/* Fine-tune text: per-field colour, size, bold, italic */}
                                            <div>
                                                <button
                                                    type="button"
                                                    onClick={() => setShowFineTune(!showFineTune)}
                                                    aria-expanded={showFineTune}
                                                    className="flex items-center justify-between w-full text-xs font-medium text-gray-600"
                                                >
                                                    <span>{t("homeCard.fineTune")}</span>
                                                    {showFineTune ? <UpOutlined className="text-xs" /> : <DownOutlined className="text-xs" />}
                                                </button>
                                                {showFineTune && (
                                                    <div data-testid="fine-tune" className="mt-3 space-y-4">
                                                        {[
                                                            ["name", "form.name"],
                                                            ["designation", "form.role"],
                                                            ["phone", "form.phone"],
                                                            ["email", "form.email"],
                                                            ["employeeId", "form.employeeID"],
                                                            ["companyName", "form.company"],
                                                        ].map(([field, labelKey]) => (
                                                            <TextStyleControl
                                                                key={field}
                                                                label={t(labelKey)}
                                                                field={field}
                                                                cardData={cardData}
                                                                setCardData={setCardDataTracked}
                                                            />
                                                        ))}
                                                        <TextStyleControl
                                                            label={
                                                                <span>
                                                                    {t("form.titleColor")}
                                                                    <Tooltip title={t("form.appliesOnlyForAppleWallet")}>
                                                                        <InfoCircleOutlined style={{ color: "#22c55e", marginLeft: 6, cursor: "pointer" }} />
                                                                    </Tooltip>
                                                                </span>
                                                            }
                                                            field="titleColor"
                                                            cardData={cardData}
                                                            setCardData={setCardDataTracked}
                                                            fontSize={false}
                                                            fontWeight={false}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Card + Buttons Column */}
                            <div className="flex flex-col items-center w-full sm:w-auto">
                                {/* Digital Card with Inline Editing */}
                                <div className="w-full sm:w-96 lg:w-[420px] xl:w-[460px]">
                                    <div className="flex justify-center mb-3">
                                        <button
                                            type="button"
                                            onClick={() => setSide((current) => (current === "en" ? "ar" : "en"))}
                                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium shadow-md transition-all active:scale-95 ${isArabicSide
                                                ? "bg-indigo-600 text-white"
                                                : "bg-white text-gray-600 hover:bg-gray-50"
                                                }`}
                                        >
                                            <SwapOutlined />
                                            <span>{isArabicSide ? t("homeCard.flip.toEnglish") : t("homeCard.flip.toArabic")}</span>
                                        </button>
                                    </div>

                                    <div style={{ perspective: reducedMotion ? undefined : 1200 }}>
                                        <div
                                            style={{
                                                display: "grid",
                                                transformStyle: reducedMotion ? undefined : "preserve-3d",
                                                transition: reducedMotion ? undefined : "transform 0.6s",
                                                transform: !reducedMotion && isArabicSide ? "rotateY(180deg)" : undefined,
                                            }}
                                        >
                                            <div
                                                data-testid="face-en"
                                                aria-hidden={isArabicSide ? "true" : undefined}
                                                inert={isArabicSide ? "" : undefined}
                                                style={faceStyle(!isArabicSide, false)}
                                            >
                                                <HomeCardFace
                                                    cardData={cardData}
                                                    texts={{
                                                        name: cardData?.name,
                                                        designation: cardData?.designation,
                                                        companyName: cardData?.companyName,
                                                    }}
                                                    dir="ltr"
                                                    placeholders={placeholders}
                                                    onTextChange={handleChange}
                                                    onSharedChange={handleChange}
                                                    onLogoChange={handleLogoImageChange}
                                                    onProfileChange={handleProfileImageChange}
                                                    isImageLoading={isImageLoading}
                                                    imageType={imageType}
                                                    socialLinks={socialLinks}
                                                    salutation={{
                                                        value: formData?.salutation || "",
                                                        options: salutations,
                                                        onChange: handleSalutationChange,
                                                        label: t("form.title"),
                                                        emptyLabel: t("form.select"),
                                                    }}
                                                />
                                            </div>
                                            <div
                                                data-testid="face-ar"
                                                aria-hidden={!isArabicSide ? "true" : undefined}
                                                inert={!isArabicSide ? "" : undefined}
                                                style={faceStyle(isArabicSide, true)}
                                            >
                                                <HomeCardFace
                                                    cardData={cardData}
                                                    texts={arabicTexts}
                                                    dir="rtl"
                                                    placeholders={{
                                                        ...placeholders,
                                                        name: t("homeCard.flip.placeholders.name"),
                                                        designation: t("homeCard.flip.placeholders.designation"),
                                                        companyName: t("homeCard.flip.placeholders.companyName"),
                                                    }}
                                                    onTextChange={handleArabicTextChange}
                                                    onSharedChange={handleChange}
                                                    onLogoChange={handleLogoImageChange}
                                                    onProfileChange={handleProfileImageChange}
                                                    isImageLoading={isImageLoading}
                                                    imageType={imageType}
                                                    socialLinks={socialLinks}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {isArabicSide && (
                                        <p className="mt-3 text-xs text-gray-500 text-center">
                                            {t("homeCard.flip.arabicHint")}
                                        </p>
                                    )}
                                </div>

                                {/* Action Buttons: Preview & Save - At the bottom */}
                                <div className="w-full sm:w-96 lg:w-[420px] xl:w-[460px] mt-4 lg:mt-6 flex gap-3 lg:gap-4">
                                    <button
                                        type="button"
                                        onClick={() => setIsPreviewOpen(true)}
                                        className="flex-1 py-3 lg:py-3.5 px-4 lg:px-6 bg-gray-600 hover:bg-gray-700 active:bg-gray-800 text-white rounded-lg lg:rounded-xl flex items-center justify-center gap-2 transition-colors text-sm sm:text-base lg:text-lg font-medium shadow-md lg:shadow-lg hover:shadow-xl"
                                    >
                                        <EyeOutlined className="text-lg lg:text-xl" />
                                        <span>{t("buttons.preview") || "Preview"}</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleSubmit}
                                        disabled={isSubmitting || !hasChanges}
                                        className={`flex-1 py-3 lg:py-3.5 px-4 lg:px-6 rounded-lg lg:rounded-xl flex items-center justify-center gap-2 transition-colors text-sm sm:text-base lg:text-lg font-medium shadow-md lg:shadow-lg hover:shadow-xl ${hasChanges
                                            ? "bg-green-500 hover:bg-green-600 active:bg-green-700 text-white"
                                            : "bg-gray-300 text-gray-500 cursor-not-allowed"
                                            }`}
                                    >
                                        <SaveOutlined className="text-lg lg:text-xl" />
                                        <span>
                                            {isSubmitting
                                                ? t("common.saving") || "Saving..."
                                                : t("buttons.save") || "Save Changes"}
                                        </span>
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Dashboard colors (the former Theme Settings screen) */}
                        <Drawer
                            title={t("homeCard.dashboardColors")}
                            open={showDashboardColors}
                            onClose={() => setShowDashboardColors(false)}
                            width={420}
                        >
                            <ThemeCustomizer />
                        </Drawer>

                        {/* Social profiles modal */}
                        <Modal
                            title={t("homeCard.social.title") || "Social profiles"}
                            open={socialModalOpen}
                            onCancel={() => setSocialModalOpen(false)}
                            footer={null}
                        >
                            <SocialProfilesEditor links={socialLinks} onChange={handleSocialChange} />
                        </Modal>

                        {/* Card Preview Modal */}
                        {cardData && (
                            <CardPreview
                                id={authCtx?.profile?.publicEmployeeId}
                                name={previewCardData?.name}
                                isOpen={isPreviewOpen}
                                cardData={previewCardData}
                                isArabic={isArabicSide}
                                onClose={() => setIsPreviewOpen(false)}
                                isIndividual={true}
                            />
                        )}
                    </>
                )}

                {/* --- Condition 2: Show Business Card Scanner --- */}
                {activeTab === 'businessCard' && (
                    <div className="w-full max-w-4xl animate-fadeIn mt-6">
                        <BusinessCardScanner />
                    </div>
                )}
            </div>
        </div>


    );
};

export default HomeCard;